import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { Contact, ContactRole, ContactSource } from "@career-ops-amplify/shared";
import {
  useContacts,
  useDeleteContact,
  useLogContactInteraction,
} from "@/hooks/useContacts";
import { ContactForm } from "@/components/companies/contact-panel";
import { ContactEditModal } from "@/components/network/contact-edit-modal";
import { FollowUpPanel } from "@/components/network/followup-panel";
import { useClientPagination } from "@/hooks/usePagination";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { useSyncCursorPage } from "@/hooks/useSyncCursorPage";
import { PaginationBar } from "@/components/pagination-bar";
import { cn } from "@/cn";
import {
  CollectionCommandBar,
  DataTable,
  EntityCard,
  EntityCompactItem,
  EntityListLayout,
  useClientTableSort,
  useListViewMode,
  type Column,
  type EntityFilterSelectField,
  type EntitySortOption,
} from "@/components/collection";

const ROLES: Array<ContactRole | ""> = [
  "",
  "recruiter",
  "hiring_manager",
  "referrer",
  "interviewer",
  "other",
];

const SOURCES: Array<ContactSource | ""> = [
  "",
  "manual",
  "contacto",
  "import",
  "interview",
  "referral",
];

const ALLOWED_MODES = ["table", "list", "cards"] as const;

const SORT_OPTIONS: EntitySortOption[] = [
  { field: "name", order: "asc", label: "Name A–Z" },
  { field: "company", order: "asc", label: "Company" },
  { field: "lastContact", order: "desc", label: "Last contact" },
];

function applySort(contacts: Contact[], sort: EntitySortOption): Contact[] {
  const dir = sort.order === "asc" ? 1 : -1;
  return [...contacts].sort((a, b) => {
    if (sort.field === "company") {
      return (a.companySlug ?? "").localeCompare(b.companySlug ?? "") * dir;
    }
    if (sort.field === "lastContact") {
      const at = a.lastInteractionAt
        ? new Date(a.lastInteractionAt).getTime()
        : 0;
      const bt = b.lastInteractionAt
        ? new Date(b.lastInteractionAt).getTime()
        : 0;
      return (at - bt) * dir;
    }
    return a.name.localeCompare(b.name) * dir;
  });
}

export function NetworkPage() {
  const [roleFilter, setRoleFilter] = useState<ContactRole | "">("");
  const [sourceFilter, setSourceFilter] = useState<ContactSource | "">("");
  const [search, setSearch] = useState("");
  const [sortOption, setSortOption] = useState<EntitySortOption>(SORT_OPTIONS[0]);
  const [editing, setEditing] = useState<Contact | null>(null);
  const [loggingId, setLoggingId] = useState<string | null>(null);
  const [interactionNotes, setInteractionNotes] = useState("");
  const [interactionChannel, setInteractionChannel] = useState("linkedin");
  const [addCompanySlug, setAddCompanySlug] = useState("");

  const { viewMode, setViewMode, allowedModes } = useListViewMode({
    storageKey: "viewMode_network",
    defaultMode: "table",
    allowedModes: [...ALLOWED_MODES],
  });

  const useServerCursor =
    !search.trim() && !roleFilter && !sourceFilter;

  const cursorPag = useCursorPagination([]);

  const filters = useMemo(
    () =>
      useServerCursor
        ? {
            limit: cursorPag.pageSize,
            cursor: cursorPag.cursor,
          }
        : {
            role: roleFilter || undefined,
            source: sourceFilter || undefined,
            search: search.trim() || undefined,
          },
    [
      useServerCursor,
      cursorPag.pageSize,
      cursorPag.cursor,
      roleFilter,
      sourceFilter,
      search,
    ],
  );

  const { data, isLoading, refetch } = useContacts(filters);
  const deleteContact = useDeleteContact();
  const logInteraction = useLogContactInteraction();

  useSyncCursorPage(
    cursorPag.setNextCursor,
    data,
    useServerCursor && !isLoading && !!data,
  );

  const contacts = useMemo(
    () => applySort(data?.contacts ?? [], sortOption),
    [data?.contacts, sortOption],
  );

  const clientPag = useClientPagination(
    useServerCursor ? [] : contacts,
    [roleFilter, sourceFilter, search, sortOption.field, sortOption.order],
  );

  const pageItems = useServerCursor ? contacts : clientPag.pageItems;
  const getSortValue = useCallback((c: Contact, key: string) => {
    if (key === "company") return c.companySlug ?? "";
    if (key === "role") return c.role ?? "";
    if (key === "lastContact") {
      return c.lastInteractionAt
        ? new Date(c.lastInteractionAt).getTime()
        : 0;
    }
    return c.name;
  }, []);

  const { sortedItems, sortKey, sortDirection, onSortChange } =
    useClientTableSort({
      items: pageItems,
      defaultKey: null,
      getValue: getSortValue,
    });

  async function handleDelete(contact: Contact) {
    if (!confirm(`Delete ${contact.name}?`)) return;
    await deleteContact.mutateAsync(contact.id);
  }

  async function handleLogInteraction(contactId: string) {
    await logInteraction.mutateAsync({
      id: contactId,
      notes: interactionNotes.trim() || undefined,
      channel: interactionChannel,
    });
    setLoggingId(null);
    setInteractionNotes("");
  }

  const filterFields: EntityFilterSelectField[] = [
    {
      id: "role",
      label: "Role",
      value: roleFilter,
      allLabel: "All roles",
      options: ROLES.filter(Boolean).map((r) => ({
        value: r,
        label: r.replace("_", " "),
      })),
      onChange: (v) => setRoleFilter(v as ContactRole | ""),
    },
    {
      id: "source",
      label: "Source",
      value: sourceFilter,
      allLabel: "All sources",
      options: SOURCES.filter(Boolean).map((s) => ({
        value: s,
        label: s,
      })),
      onChange: (v) => setSourceFilter(v as ContactSource | ""),
    },
  ];

  const renderActions = (c: Contact) => (
    <div
      className="flex shrink-0 flex-wrap gap-2"
      onClick={(e) => e.stopPropagation()}
    >
      {c.email && (
        <a href={`mailto:${c.email}`} className="text-xs text-brand hover:underline">
          Email
        </a>
      )}
      {c.linkedinUrl && (
        <a
          href={c.linkedinUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-brand hover:underline"
        >
          LinkedIn
        </a>
      )}
      <button
        type="button"
        onClick={() => setLoggingId(c.id)}
        className="text-xs text-muted-foreground hover:text-foreground"
      >
        Log touch
      </button>
      <button
        type="button"
        onClick={() => setEditing(c)}
        className="text-xs text-muted-foreground hover:text-foreground"
      >
        Edit
      </button>
      <button
        type="button"
        onClick={() => handleDelete(c)}
        className="text-xs text-red-500 hover:underline"
      >
        Delete
      </button>
    </div>
  );

  const logPanel = (c: Contact) =>
    loggingId === c.id ? (
      <div
        className="mt-3 space-y-2 rounded-md border border-border bg-muted/10 p-3"
        onClick={(e) => e.stopPropagation()}
      >
        <select
          value={interactionChannel}
          onChange={(e) => setInteractionChannel(e.target.value)}
          className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
        >
          <option value="linkedin">LinkedIn</option>
          <option value="email">Email</option>
          <option value="phone">Phone</option>
        </select>
        <textarea
          value={interactionNotes}
          onChange={(e) => setInteractionNotes(e.target.value)}
          rows={2}
          placeholder="Outreach notes…"
          className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => handleLogInteraction(c.id)}
            disabled={logInteraction.isPending}
            className={cn(
              "rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground",
              logInteraction.isPending && "opacity-50",
            )}
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => setLoggingId(null)}
            className="rounded-md border border-border px-3 py-1.5 text-xs"
          >
            Cancel
          </button>
        </div>
      </div>
    ) : null;

  const columns: Column<Contact>[] = [
    {
      key: "name",
      header: "Name",
      sortable: true,
      render: (c) => <span className="font-medium text-foreground">{c.name}</span>,
    },
    {
      key: "role",
      header: "Role",
      sortable: true,
      render: (c) => (
        <span className="text-muted-foreground">
          {[c.role?.replace("_", " "), c.title].filter(Boolean).join(" · ") ||
            "—"}
        </span>
      ),
    },
    {
      key: "company",
      header: "Company",
      sortable: true,
      render: (c) => (
        <Link
          to={`/companies/${c.companySlug}`}
          onClick={(e) => e.stopPropagation()}
          className="text-brand hover:underline"
        >
          {c.companySlug}
        </Link>
      ),
    },
    {
      key: "lastContact",
      header: "Last touch",
      sortable: true,
      render: (c) => (
        <span className="text-muted-foreground">
          {c.lastInteractionAt
            ? new Date(c.lastInteractionAt).toLocaleDateString()
            : "—"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      className: "text-right",
      render: (c) => renderActions(c),
    },
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 md:p-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Network</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Recruiters, referrers, and hiring contacts across your search.
          </p>
        </div>
        <Link
          to="/network/intake"
          className="rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-muted/20"
        >
          Batch intake
        </Link>
      </header>

      <CollectionCommandBar
        searchQuery={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search name, email, company…"
        sortOption={sortOption}
        onSortChange={setSortOption}
        sortOptions={SORT_OPTIONS}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        allowedModes={allowedModes}
        filterFields={filterFields}
        activeFilterCount={(roleFilter ? 1 : 0) + (sourceFilter ? 1 : 0)}
        onClear={() => {
          setSearch("");
          setRoleFilter("");
          setSourceFilter("");
        }}
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="space-y-3 lg:col-span-2">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading contacts…</p>
          ) : contacts.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No contacts yet. Add one below or save targets from a contacto run
              in Pipeline.
            </p>
          ) : (
            <>
              <EntityListLayout
                viewMode={viewMode}
                table={
                  <DataTable
                    data={sortedItems}
                    columns={columns}
                    sortKey={sortKey}
                    sortDirection={sortDirection}
                    onSortChange={onSortChange}
                  />
                }
                list={
                  <div className="space-y-2">
                    {pageItems.map((c) => (
                      <div key={c.id}>
                        <EntityCompactItem
                          id={c.id}
                          title={c.name}
                          description={[
                            c.role?.replace("_", " "),
                            c.title,
                            c.agencyName ? `@ ${c.agencyName}` : undefined,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                          metadata={[
                            {
                              value: (
                                <>
                                  <Link
                                    to={`/companies/${c.companySlug}`}
                                    className="text-brand hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {c.companySlug}
                                  </Link>
                                  {c.source && ` · ${c.source}`}
                                  {c.lastInteractionAt &&
                                    ` · last touch ${new Date(c.lastInteractionAt).toLocaleDateString()}`}
                                </>
                              ),
                            },
                          ]}
                          actions={renderActions(c)}
                        />
                        {logPanel(c)}
                      </div>
                    ))}
                  </div>
                }
                cards={
                  <div className="grid gap-3 sm:grid-cols-2">
                    {pageItems.map((c) => (
                      <div key={c.id}>
                        <EntityCard
                          id={c.id}
                          title={c.name}
                          description={[
                            c.role?.replace("_", " "),
                            c.title,
                            c.agencyName ? `@ ${c.agencyName}` : undefined,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                          metadata={[
                            {
                              value: (
                                <Link
                                  to={`/companies/${c.companySlug}`}
                                  className="text-brand hover:underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {c.companySlug}
                                </Link>
                              ),
                            },
                            ...(c.lastInteractionAt
                              ? [
                                  {
                                    value: `Last touch ${new Date(c.lastInteractionAt).toLocaleDateString()}`,
                                  },
                                ]
                              : []),
                          ]}
                          actions={renderActions(c)}
                        />
                        {logPanel(c)}
                      </div>
                    ))}
                  </div>
                }
              />
              {useServerCursor ? (
                <PaginationBar
                  mode="cursor"
                  page={cursorPag.page}
                  pageSize={cursorPag.pageSize}
                  itemCount={pageItems.length}
                  showingFrom={
                    pageItems.length === 0 ? 0 : cursorPag.showingFrom
                  }
                  showingTo={cursorPag.showingTo(pageItems.length)}
                  hasMore={cursorPag.hasMore}
                  canPrev={cursorPag.canPrev}
                  onFirst={cursorPag.goFirst}
                  onPrev={cursorPag.goPrev}
                  onNext={cursorPag.goNext}
                  onPageSizeChange={cursorPag.setPageSize}
                />
              ) : (
                <PaginationBar
                  page={clientPag.page}
                  pageSize={clientPag.pageSize}
                  totalItems={contacts.length}
                  totalPages={clientPag.totalPages}
                  showingFrom={clientPag.showingFrom}
                  showingTo={clientPag.showingTo}
                  onPageChange={clientPag.setPage}
                  onPageSizeChange={clientPag.setPageSize}
                />
              )}
            </>
          )}
        </section>

        <aside className="space-y-4">
          <section className="rounded-lg border border-border bg-card p-4">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Add contact
            </h3>
            <input
              type="text"
              placeholder="Company slug (e.g. stripe)"
              value={addCompanySlug}
              onChange={(e) => setAddCompanySlug(e.target.value)}
              className="mb-3 w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
            />
            {addCompanySlug.trim() ? (
              <ContactForm
                companySlug={addCompanySlug.trim()}
                onCreated={() => {
                  refetch();
                  setAddCompanySlug("");
                }}
              />
            ) : (
              <p className="text-xs text-muted-foreground">
                Enter a company slug to add a contact.
              </p>
            )}
          </section>
          <FollowUpPanel compact />
        </aside>
      </div>

      {editing && (
        <ContactEditModal
          contact={editing}
          onClose={() => setEditing(null)}
          onSaved={() => refetch()}
        />
      )}
    </div>
  );
}
